import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  computed,
  effect,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import cytoscape, { Core, EventObject } from 'cytoscape';
import '../../../core/cytoscape-setup';
import { Flow, Governs, Policy } from '../../../core/services/policy-flow';

@Component({
  selector: 'app-flow-view',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './flow-view.html',
  styleUrl: './flow-view.css',
})
export class FlowView implements AfterViewInit, OnDestroy {
  @Input() set flows(value: Flow[]) {
    this._flows.set(value ?? []);
    // Keep a valid selection when the flow list changes (e.g. after a rebuild).
    const current = this.selectedFlowId();
    const list = value ?? [];
    if (!current || !list.some(f => f.flow_id === current)) {
      this.selectedFlowId.set(list[0]?.flow_id ?? null);
    }
  }
  @Input() set governs(value: Governs[]) {
    this._governs.set(value ?? []);
  }
  @Input() set policies(value: Policy[]) {
    this._policies.set(value ?? []);
  }
  /** flow_ids with a setReview call in flight. */
  @Input() set pendingIds(value: Set<string>) {
    this._pendingIds.set(value ?? new Set<string>());
  }

  @Output() reviewFlow = new EventEmitter<{ id: string; review: boolean }>();

  @ViewChild('flowCanvas') flowCanvas?: ElementRef<HTMLDivElement>;

  private _flows = signal<Flow[]>([]);
  private _governs = signal<Governs[]>([]);
  private _policies = signal<Policy[]>([]);
  private _pendingIds = signal<Set<string>>(new Set<string>());

  selectedFlowId = signal<string | null>(null);
  selectedStepId = signal<string | null>(null);

  private cy: Core | null = null;
  private viewReady = false;

  flowsList = computed(() => this._flows());

  selectedFlow = computed<Flow | null>(() => {
    const id = this.selectedFlowId();
    return this._flows().find(f => f.flow_id === id) ?? null;
  });

  selectedStepName = computed(() => {
    const f = this.selectedFlow();
    const sid = this.selectedStepId();
    return f?.steps.find(s => s.step_id === sid)?.name ?? '';
  });

  /** Policies governing the currently-selected step (join governs on step_id). */
  stepPolicies = computed<Policy[]>(() => {
    const sid = this.selectedStepId();
    if (!sid) return [];
    const policyIds = new Set(
      this._governs().filter(g => g.step_id === sid).map(g => g.policy_id)
    );
    return this._policies().filter(p => policyIds.has(p.policy_id));
  });

  constructor() {
    // Re-render the diagram whenever the selected flow changes.
    effect(() => {
      this.selectedFlowId();
      if (this.viewReady) this.renderFlow();
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderFlow();
  }

  ngOnDestroy(): void {
    this.cy?.destroy();
    this.cy = null;
  }

  selectFlow(id: string) {
    if (id === this.selectedFlowId()) return;
    this.selectedStepId.set(null);
    this.selectedFlowId.set(id);
  }

  stepCount(f: Flow): number {
    return f.steps.length;
  }

  policyCountFor(f: Flow): number {
    const stepIds = new Set(f.steps.map(s => s.step_id));
    const policyIds = new Set(
      this._governs().filter(g => stepIds.has(g.step_id)).map(g => g.policy_id)
    );
    return policyIds.size;
  }

  isPending(id: string | null | undefined): boolean {
    return !!id && this._pendingIds().has(id);
  }

  onToggleFlowReview() {
    const f = this.selectedFlow();
    if (!f || this.isPending(f.flow_id)) return;
    this.reviewFlow.emit({ id: f.flow_id, review: !f.review });
  }

  trackByFlow(_: number, f: Flow) {
    return f.flow_id;
  }

  // ---- diagram ----

  private ensureCy(): Core | null {
    const container = this.flowCanvas?.nativeElement;
    if (!container) return null;
    if (this.cy) return this.cy;

    this.cy = cytoscape({
      container,
      elements: [],
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#FFFFFF',
            'border-color': '#6366F1',
            'border-width': 1.5,
            label: 'data(label)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '11px',
            color: '#0F172A',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '150px',
            shape: 'roundrectangle',
            width: 170,
            height: 50,
            padding: '8px',
          },
        },
        {
          selector: 'node.selected',
          style: {
            'border-width': 3,
            'border-color': '#F59E0B',
            'background-color': '#FFFBEB',
          },
        },
        {
          selector: 'edge',
          style: {
            width: 1.6,
            'line-color': '#94A3B8',
            'target-arrow-color': '#94A3B8',
            'target-arrow-shape': 'triangle',
            'arrow-scale': 0.9,
            'curve-style': 'bezier',
            label: 'data(label)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '9px',
            color: '#64748B',
            'text-background-color': '#F4F5F9',
            'text-background-opacity': 1,
            'text-background-padding': '2px',
            'text-rotation': 'autorotate',
          },
        },
      ],
      layout: { name: 'preset' },
      minZoom: 0.3,
      maxZoom: 2.5,
      wheelSensitivity: 0.2,
    });

    this.cy.on('tap', 'node', (ev: EventObject) => {
      const id = ev.target.id();
      this.selectedStepId.set(id);
      this.cy?.nodes().removeClass('selected');
      ev.target.addClass('selected');
    });

    this.cy.on('tap', (ev: EventObject) => {
      if (ev.target === this.cy) {
        this.selectedStepId.set(null);
        this.cy?.nodes().removeClass('selected');
      }
    });

    return this.cy;
  }

  private renderFlow() {
    const cy = this.ensureCy();
    const flow = this.selectedFlow();
    if (!cy) return;

    cy.elements().remove();
    if (!flow) return;

    const positions = this.layout(flow);
    const COL = 240;
    const ROW = 84;

    cy.add(
      flow.steps.map(s => ({
        group: 'nodes' as const,
        data: { id: s.step_id, label: s.name },
        position: {
          x: positions[s.step_id].layer * COL,
          y: positions[s.step_id].row * ROW,
        },
      }))
    );

    const stepIds = new Set(flow.steps.map(s => s.step_id));
    cy.add(
      flow.transitions
        .filter(t => stepIds.has(t.from_step_id) && stepIds.has(t.to_step_id))
        .map((t, i) => ({
          group: 'edges' as const,
          data: {
            id: `e${i}`,
            source: t.from_step_id,
            target: t.to_step_id,
            label: t.condition ?? '',
          },
        }))
    );

    cy.fit(undefined, 40);
  }

  /**
   * Longest-path layering for a left-to-right DAG: roots (no incoming
   * transition) sit in layer 0; each node's layer is 1 + the max layer of its
   * predecessors. Nodes within a layer are stacked by arrival order.
   */
  private layout(flow: Flow): Record<string, { layer: number; row: number }> {
    const incoming: Record<string, number> = {};
    const adj: Record<string, string[]> = {};
    flow.steps.forEach(s => {
      incoming[s.step_id] = 0;
      adj[s.step_id] = [];
    });
    flow.transitions.forEach(t => {
      if (adj[t.from_step_id] && incoming[t.to_step_id] !== undefined) {
        adj[t.from_step_id].push(t.to_step_id);
        incoming[t.to_step_id]++;
      }
    });

    const layer: Record<string, number> = {};
    const roots = flow.steps.filter(s => incoming[s.step_id] === 0).map(s => s.step_id);
    const queue = [...roots];
    roots.forEach(r => (layer[r] = 0));

    let guard = 0;
    while (queue.length && guard++ < 10000) {
      const n = queue.shift()!;
      for (const m of adj[n] ?? []) {
        layer[m] = Math.max(layer[m] ?? 0, (layer[n] ?? 0) + 1);
        queue.push(m);
      }
    }

    const rowCursor: Record<number, number> = {};
    const result: Record<string, { layer: number; row: number }> = {};
    flow.steps.forEach(s => {
      const l = layer[s.step_id] ?? 0;
      const row = rowCursor[l] ?? 0;
      rowCursor[l] = row + 1;
      result[s.step_id] = { layer: l, row };
    });
    return result;
  }
}
